import * as React from "react";
import { IImpactSettings } from "./business-objects/AreaOfDamageOptions";
const $ = require("jquery");
import "./scripts/maphilight_full.js";
import { SelectType, FillColor } from "./Enum";

interface IAreaOfDamageImpactPointsOwnProps {
  component: any;
  imageSrc: string;
  value: string | string[];
  selectType: SelectType;
  impactSettings: IImpactSettings[];
  idPrefix?: string; // unique per instance
}

interface IAreasOfDamageImpactPointsActionProps {
  inputChanged: (value: string) => void;
}

interface IAreaOfDamageImpactPointsProps
  extends IAreasOfDamageImpactPointsActionProps,
    IAreaOfDamageImpactPointsOwnProps {}

export default class AreaOfDamageImpactPoints extends React.Component<IAreaOfDamageImpactPointsProps> {
  private imgRef = React.createRef<HTMLImageElement>();
  private mapRef = React.createRef<HTMLMapElement>();
  private uid: string;

  constructor(props: IAreaOfDamageImpactPointsProps) {
    super(props);
    const rnd = AreaOfDamageImpactPoints.generateSecureId();
    this.uid = props.idPrefix || `${props.component?.key || "aod"}_${rnd}`;
  }

  private static generateSecureId(): string {
    const cryptoObj: Crypto | undefined =
      typeof globalThis !== "undefined" ? (globalThis as any).crypto : undefined;
    const bytes = new Uint8Array(6);
    if (cryptoObj?.getRandomValues) {
      cryptoObj.getRandomValues(bytes);
    }
    let out = "";
    for (let i = 0; i < bytes.length; i++) {
      out += (bytes[i] % 36).toString(36);
    }
    return out;
  }

  componentDidMount() {
    this.initMaphilight();
    this.applyHighlights();
    // Fallback: delegated jQuery handler in case React's area events are swallowed
    $(this.mapRef.current as any).on("click", "area", this.onDelegatedAreaClick);
  }

  componentDidUpdate() {
    this.initMaphilight();
    this.applyHighlights();
  }

  componentWillUnmount() {
    $(this.mapRef.current as any).off("click", "area", this.onDelegatedAreaClick);
  }

  private toArray = (val: string | string[] | undefined): string[] =>
    !val ? [] : Array.isArray(val) ? val.map(String) : [String(val)];

  private isSelected = (id: string) => this.toArray(this.props.value).includes(String(id));

  private getSelectedValue = (id: string): string => {
    console.log("map clicked value " + id)
    if (this.props.selectType === SelectType.Single) {
      return this.isSelected(id) ? "" : id; // toggle for single
    }
    return id; // multi handled by parent
  };

  private onDelegatedAreaClick = (ev: any) => {
    ev.preventDefault();
    const id = String(ev.currentTarget?.dataset?.id || ev.currentTarget?.alt || "");
    if (!id) return;
    this.props.inputChanged(this.getSelectedValue(id));
  };

  private onAreaClick = (e: React.MouseEvent<HTMLAreaElement>) => {
    e.preventDefault();
    const id = String((e.currentTarget as any).dataset.id || e.currentTarget.alt || "");
    if (!id) return;
    this.props.inputChanged(this.getSelectedValue(id));
  };

  private onAreaMouseDown = (e: React.MouseEvent<HTMLAreaElement>) => {
    // Some browsers don't deliver click for <area> reliably when a canvas overlay exists
    e.preventDefault();
    const id = String((e.currentTarget as any).dataset.id || e.currentTarget.alt || "");
    if (!id) return;
    this.props.inputChanged(this.getSelectedValue(id));
  };

  private onAreaKeyDown = (e: React.KeyboardEvent<HTMLAreaElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const id = String((e.currentTarget as any).dataset.id || e.currentTarget.alt || "");
      if (!id) return;
      this.props.inputChanged(this.getSelectedValue(id));
    }
  };

  private initMaphilight = () => {
    const $img = $(this.imgRef.current as any);
    if (!$img.length) return;
    $img.maphilight({
      strokeColor: FillColor.unselect,
      strokeWidth: 0,
      fillOpacity: 0.5,
      fill: true,
      fillColor: FillColor.unselect,
      alwaysOn: true
    });
  };

  private getHighlightData = (id: string) => {
    const selected = this.isSelected(id);
    return {
      fill: true,
      fillOpacity: 0.5,
      strokeColor: selected ? FillColor.select : FillColor.unselect,
      fillColor: selected ? FillColor.select : FillColor.unselect
    };
  };

  private applyHighlights = () => {
    this.props.impactSettings.forEach((setting: IImpactSettings) => {
      const areaId = `#${this.uid}_area_${setting.id}`;
      const $area = $(areaId);
      if ($area.length) {
        $area.data("maphilight", this.getHighlightData(setting.id)).trigger("alwaysOn.maphilight");
      }
    });
  };

  render() {
    const mapName = `${this.uid}_impactPoints`;

    return (
      <div style={{ marginLeft: "151px", marginTop: "-25px" }}>
        <img
          className="map"
          ref={this.imgRef}
          id={`${this.uid}_img`}
          src={this.props.imageSrc}
          width={this.props.component.width}
          height={this.props.component.height}
          useMap={`#${mapName}`}
          alt={this.props.component.label}
        />
        <map id={mapName} name={mapName} ref={this.mapRef}>
          {this.props.impactSettings.map(impact =>
            !impact.disabled ? (
              <area
                key={`${this.uid}_area_${impact.id}`}
                id={`${this.uid}_area_${impact.id}`}
                data-id={impact.id}
                shape="rect"
                coords={impact.value}
                alt={impact.id}
                href="#"
                role="button"
                tabIndex={0}
                onClick={this.onAreaClick}
                onMouseDown={this.onAreaMouseDown}
                onKeyDown={this.onAreaKeyDown}
                style={{ cursor: "pointer" }}
              />
            ) : null
          )}
        </map>
      </div>
    );
  }
}
